import { useState, type KeyboardEvent } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  type SelectChangeEvent,
} from '@mui/material';
import { Plus } from 'lucide-react';
import { useSnackbar } from 'notistack';

import { AUTO_DIRECTOR_CAMERA, AUTO_DIRECTOR_CAMERA_LABEL } from '@/features/clips/types';

import {
  useCreateWscStream,
  useGameCameras,
  useGameTrackedEntities,
} from '../hooks/wscStreamQueries';
import type { WscStreamTrackedEntityType } from '../types';
import { WscBadge } from './WscBadge';

interface Props {
  gameUuid: string;
}

const OBJECT_OF_PLAY_ID = 1;

/**
 * The camera dropdown holds gameCamera ids plus one sentinel entry. Reuses the
 * clips sentinel so "Auto-Director" means the same thing in both flows: the
 * player cuts between every camera instead of streaming a fixed one.
 */
type CameraSelection = number | '' | typeof AUTO_DIRECTOR_CAMERA;

// BuildWscStreamerConfig needs at least two inputs to cut between, and the
// create endpoint 400s below that — so don't offer the option at all.
const AUTO_DIRECTOR_MIN_CAMERAS = 2;

export function AddWscStreamForm({ gameUuid }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const createStream = useCreateWscStream(gameUuid);
  const { data: cameras = [], isLoading: camerasLoading } = useGameCameras(gameUuid);
  const { data: trackedEntities, isLoading: entitiesLoading } = useGameTrackedEntities(gameUuid);

  const [cameraId, setCameraId] = useState<CameraSelection>('');
  const [entityId, setEntityId] = useState<number | ''>(OBJECT_OF_PLAY_ID);
  const [wsc, setWsc] = useState(false);

  const entities = trackedEntities?.entities ?? [];
  const isReady = !camerasLoading && !entitiesLoading;
  const isAutoDirector = cameraId === AUTO_DIRECTOR_CAMERA;
  const showAutoDirector = cameras.length >= AUTO_DIRECTOR_MIN_CAMERAS;
  const canSubmit = isReady && cameraId !== '' && entityId !== '' && !createStream.isPending;

  const submit = () => {
    if (!canSubmit) return;
    const entity = entities.find((e) => e.id === entityId);
    const trackedEntityType: WscStreamTrackedEntityType = entity?.type ?? 'BallOrPuck';

    createStream.mutate(
      {
        // Exactly one of these — the API rejects a payload carrying both.
        ...(isAutoDirector
          ? { autoDirector: true }
          : { gameCameraId: Number(cameraId) }),
        trackedEntityId: Number(entityId),
        trackedEntityType,
        wsc,
      },
      {
        onSuccess: (created) => {
          enqueueSnackbar(`Stream #${created.id} scheduled`, { variant: 'success' });
          setCameraId('');
          setEntityId(OBJECT_OF_PLAY_ID);
          setWsc(false);
        },
        onError: (err: any) => {
          const msg = err?.response?.data?.message ?? err?.message ?? 'Create failed';
          enqueueSnackbar(msg, { variant: 'error' });
        },
      },
    );
  };

  // Pressing Enter while any field is focused submits, matching the
  // user-stated UX: "by pressing enter he will schedule a new wsc stream."
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submit();
    }
  };

  return (
    <Box
      onKeyDown={handleKeyDown}
      sx={{ p: 2, bgcolor: 'background.default', borderTop: '1px dashed', borderColor: 'divider' }}
    >
      <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id={`camera-${gameUuid}`}>Camera</InputLabel>
          <Select
            labelId={`camera-${gameUuid}`}
            label="Camera"
            value={cameraId}
            onChange={(e: SelectChangeEvent<CameraSelection>) => {
              const next = e.target.value;
              setCameraId(
                next === '' || next === AUTO_DIRECTOR_CAMERA ? next : Number(next),
              );
            }}
            disabled={!isReady}
          >
            {showAutoDirector && (
              <MenuItem value={AUTO_DIRECTOR_CAMERA}>{AUTO_DIRECTOR_CAMERA_LABEL}</MenuItem>
            )}
            {cameras.map((cam) => (
              <MenuItem key={cam.id} value={cam.id}>
                {cam.displayName ?? `camera #${cam.camIndex}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id={`entity-${gameUuid}`}>Tracking entity</InputLabel>
          <Select
            labelId={`entity-${gameUuid}`}
            label="Tracking entity"
            value={entityId}
            onChange={(e: SelectChangeEvent<number | ''>) =>
              setEntityId(e.target.value === '' ? '' : Number(e.target.value))
            }
            disabled={!isReady}
          >
            {entities.map((ent) => (
              <MenuItem key={ent.id} value={ent.id}>
                {ent.displayName}
                {ent.type === 'Player' && ent.playerNumber ? ` · #${ent.playerNumber}` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={wsc}
              onChange={(e) => setWsc(e.target.checked)}
              disabled={!isReady}
            />
          }
          label={<WscBadge />}
        />

        <Button
          variant="contained"
          size="small"
          startIcon={<Plus />}
          onClick={submit}
          disabled={!canSubmit}
        >
          Add stream
        </Button>
      </Stack>
    </Box>
  );
}
